import { useMemo } from "react";
import { Box, Column, Row, Spinner, Text } from "@hightouch/htui";
import type { DecisionEngineChannelType } from "@hightouch/lib/customer-data/decision-engine/types";

import { AccordionSection } from "src/components/accordion-section";
import { Card } from "src/components/card";
import { Link } from "src/router";
import { Table, type TableColumn } from "src/ui/table";
import { ChannelIcon } from "src/pages/decision-engines/components/channel-icon";
import {
  NO_SYNC_RECONCILIATION_DATA_MESSAGE,
  sumTotals,
  SYNC_RECONCILIATION_BUCKETS,
  totalRows,
  useSyncReconciliationByMessage,
  type SyncReconciliationTotals,
} from "src/pages/decision-engines/sync-reconciliation";

import type { LabelResolver } from "./types";

type MessageRow = {
  id: string;
  name: string;
  url: string | undefined;
  channelType: DecisionEngineChannelType | undefined;
  totals: SyncReconciliationTotals;
};

function totalDropped(totals: SyncReconciliationTotals): number {
  return totals.droppedAtSync + totals.failedAtSync;
}

type Props = {
  flowId: string;
  lookbackDays: number | undefined;
  anchorDate: string | null;
  resolveLabel: LabelResolver;
};

export function SyncDeliveryCard({
  flowId,
  lookbackDays,
  anchorDate,
  resolveLabel,
}: Props) {
  const { byMessage, hasData, isLoading } = useSyncReconciliationByMessage({
    flowId,
    windowDays: lookbackDays,
    anchorDate,
  });

  const totals = useMemo(() => sumTotals([...byMessage.values()]), [byMessage]);

  const rows = useMemo(() => {
    const messageRows: MessageRow[] = [...byMessage.entries()].map(
      ([messageId, messageTotals]) => {
        const label = resolveLabel("message_id", messageId);
        return {
          id: messageId,
          name: label.name,
          url: label.url,
          channelType: label.channelType,
          totals: messageTotals,
        };
      },
    );
    return messageRows.sort(
      (a, b) => totalDropped(b.totals) - totalDropped(a.totals),
    );
  }, [byMessage, resolveLabel]);

  const grandTotal = totalRows(totals);

  return (
    <Card gap={4} overflow="hidden">
      <Column gap={1}>
        <Text size="lg" fontWeight="medium" color="text.primary">
          Sync delivery
        </Text>
        <Text size="md" fontWeight="normal" color="text.secondary">
          Reconciles recommendations marked ready to send against the warehouse
          sync changelog. Holdout and customer-managed recommendations are
          excluded, since they never enter a sync by design.
        </Text>
      </Column>

      {isLoading ? (
        <Spinner size="sm" m="auto" />
      ) : !hasData ? (
        <Text color="text.secondary">
          {NO_SYNC_RECONCILIATION_DATA_MESSAGE}
        </Text>
      ) : grandTotal === 0 ? (
        <Text color="text.secondary">
          No recommendations were sent or dropped in the selected window.
        </Text>
      ) : (
        <Column gap={4}>
          <Column gap={3}>
            {SYNC_RECONCILIATION_BUCKETS.map(
              ({ key, label, color, description }) => (
                <SyncDeliveryBar
                  key={key}
                  label={label}
                  description={description}
                  color={color}
                  value={totals[key]}
                  total={grandTotal}
                />
              ),
            )}
          </Column>

          <AccordionSection label="By message" count={rows.length} noPadding>
            <Box
              sx={{ "& tr:last-child > td": { border: "none" } }}
              mx={-6}
              mt={2}
            >
              <Table primaryKey="id" data={rows} columns={MESSAGE_COLUMNS} />
            </Box>
          </AccordionSection>
        </Column>
      )}
    </Card>
  );
}

const MESSAGE_COLUMNS: TableColumn<MessageRow>[] = [
  {
    header: () => <Text color="text.secondary">Message</Text>,
    max: "3fr",
    cell: ({ name, url, channelType }) => (
      <Row align="center" gap={2} overflow="hidden">
        {channelType && (
          <Box flexShrink={0}>
            <ChannelIcon type={channelType} />
          </Box>
        )}
        {url ? <Link href={url}>{name}</Link> : <Text>{name}</Text>}
      </Row>
    ),
  },
  ...SYNC_RECONCILIATION_BUCKETS.map(
    ({ key, label, color, emphasizeWhenNonZero }): TableColumn<MessageRow> => ({
      header: () => <Text color="text.secondary">{label}</Text>,
      cell: ({ totals }) => (
        <Text
          color={
            emphasizeWhenNonZero && totals[key] > 0 ? color : "text.primary"
          }
        >
          {totals[key].toLocaleString()}
        </Text>
      ),
    }),
  ),
];

const SyncDeliveryBar = ({
  label,
  description,
  color,
  value,
  total,
}: {
  label: string;
  description: string;
  color: string;
  value: number;
  total: number;
}) => {
  const percent = total > 0 ? (value / total) * 100 : 0;

  return (
    <Column gap={1}>
      <Row justify="space-between" align="baseline" gap={4}>
        <Text fontWeight="medium">{label}</Text>
        <Row gap={2} align="baseline" flexShrink={0}>
          <Text fontWeight="medium">{value.toLocaleString()}</Text>
          <Text size="sm" color="text.secondary">
            {percent.toFixed(1)}%
          </Text>
        </Row>
      </Row>
      <Box bg="base.background" h={2} borderRadius="md" width="100%">
        <Box
          width={`${percent}%`}
          height="100%"
          bg={color}
          borderRadius="md"
          transitionProperty="width"
          transitionDuration="normal"
          transitionTimingFunction="easeOutQuad"
        />
      </Box>
      <Text size="sm" color="text.secondary">
        {description}
      </Text>
    </Column>
  );
};
